import { Controller } from "@hotwired/stimulus"

// The odds meter on the barter stones. Every change to the offer form is sent
// to trades#odds (Trade::Odds), debounced, and the answer — a heat of
// cold/cool/warm/hot, a score out of a hundred, and one line saying why — is
// painted onto the bar before the offer is laid. The form is the source of
// truth: the request is built from its own fields, so nothing here need know
// what the stones hold.
//
// Drawn once by the server; if the request fails the last reading stands.
const WAIT_MS = 250

export default class extends Controller {
  static targets = ["meter", "heat", "bar", "fill", "line"]
  static values = { url: String }

  disconnect() {
    clearTimeout(this.timer)
    this.inflight?.abort()
  }

  read() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.ask(), WAIT_MS)
  }

  async ask() {
    this.inflight?.abort()
    const controller = new AbortController()
    this.inflight = controller

    const query = new URLSearchParams()
    for (const [name, value] of new FormData(this.element)) {
      if (name === "authenticity_token" || name === "note" || name === "back") continue
      if (String(value).trim() === "") continue
      query.append(name, value)
    }

    this.meterTarget.setAttribute("aria-busy", "true")
    try {
      const response = await fetch(`${this.urlValue}?${query}`, {
        headers: { Accept: "application/json" },
        signal: controller.signal
      })
      if (!response.ok) return
      this.paint(await response.json())
    } catch (error) {
      if (error.name !== "AbortError") console.warn("odds:", error)
    } finally {
      if (this.inflight === controller) {
        this.inflight = null
        this.meterTarget.removeAttribute("aria-busy")
      }
    }
  }

  paint({ heat, score, line }) {
    this.meterTarget.dataset.heat = heat
    this.heatTarget.textContent = heat
    this.barTarget.setAttribute("aria-valuenow", score)
    this.fillTarget.style.setProperty("--odds-score", score)
    this.lineTarget.textContent = line
  }
}
